import { api } from "./api";

/**
 * Callbacks for remote session revocation (the three links that made the UI LIE
 * about revocation in phase 2).
 *
 * They lived inline in `providers/boot-manager.tsx`, where only an e2e could
 * cover them. The e2e for the SUCCESS path turned out to be a POORLY RELIABLE
 * detector: with `onRevokeSuccess` deliberately removed, the row still
 * disappeared in one out of two runs because `sessions-section` runs with
 * `refetchOnWindowFocus: true`, and closing the dialog can trigger the refetch
 * by itself. A guard that only bites half the time is not a guard. Extracted
 * here, `revoke-session-handlers.test.ts` pins them deterministically.
 */
export interface RevokeSessionHandlerDeps {
  /** Invalidates the active sessions list (`queryKeys.auth.sessions()`). */
  invalidateSessions: () => void;
  /** Notifies the user that revocation did NOT happen. */
  notifyError: () => void;
}

export interface RevokeSessionHandlers {
  onRevokeSession: (sessionId: string) => Promise<void>;
  onRevokeSuccess: () => void;
  onRevokeError: () => void;
}

export function createRevokeSessionHandlers(deps: RevokeSessionHandlerDeps): RevokeSessionHandlers {
  return {
    // SECURITY: `api.POST` (openapi-fetch) does NOT throw on 4xx/5xx: it returns
    // `{ error }`. Without this check, the promise resolved despite a
    // 401/403/500, and the machine exited through `onDone` — the `onError` branch
    // was dead code, and someone trying to remove a lost device was told they
    // had succeeded. Measured in e2e with the endpoint forced to 500: dialog
    // closed, zero warnings, and the device remained authenticated.
    onRevokeSession: async (sessionId: string) => {
      const { error, response } = await api.POST("/api/auth/revoke-session", {
        body: { id: sessionId },
      });
      if (error !== undefined || !response.ok) {
        throw new Error(`revoke-session failed with status ${response.status}`);
      }
    },
    // The sessions list was NOT refreshed after revocation (this callback was
    // never passed, so the machine called `undefined?.()`): the already-revoked
    // device stayed listed until the user manually pressed "Refresh".
    onRevokeSuccess: () => {
      deps.invalidateSessions();
    },
    onRevokeError: () => {
      deps.notifyError();
      deps.invalidateSessions();
    },
  };
}
